/**
 * FERP Portal - Tenant Management Dashboard (DRR-073)
 *
 * Superadmin-gated page for FERP Operations staff (tenant 1)
 * to manage customer tenant onboarding lifecycle.
 *
 * Features:
 * - List all tenants with onboarding status
 * - Onboarding pipeline view per tenant
 * - Document upload trigger for Chaos ingestion
 * - Approval queue for generated configs
 * - Activation checklist and activation trigger
 *
 * Access: architect ViewLayer only (superadmin/architect roles)
 *
 * FERP Principles:
 * - Data-driven: SystemTenant data from GraphQL
 * - Repository pattern: via Apollo Client
 * - Dumb frontend: renders backend state only
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - NO hardcoded tenant data
 * - Multi-tenant via useTenantId()
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Container,
  Title,
  Stack,
  Group,
  Card,
  Text,
  Badge,
  Alert,
  Skeleton,
  SimpleGrid,
  ThemeIcon,
  Button,
  Table,
  Tabs,
  Progress,
} from '@mantine/core';
import {
  IconBuilding,
  IconAlertCircle,
  IconShieldCheck,
  IconRocket,
  IconClock,
  IconBan,
  IconChecks,
  IconUpload,
  IconListCheck,
  IconClipboardCheck,
} from '@tabler/icons-react';
// eslint-disable-next-line no-restricted-imports -- SystemTenant queries not yet in codegen
import { useQuery, useMutation } from '@apollo/client/react';

import { useAuth, useViewLayer } from '@/auth/AuthContext';
import { useTenantId } from '@/auth';
import {
  ActivateSystemTenantDocument,
  ActiveSystemTenantsDocument,
  GqlOnboardingStatus,
  useActivateSystemTenantMutation,
  useActiveSystemTenantsQuery,
} from '@/generated/graphql';
import { DocumentUploadPanel } from './components/DocumentUploadPanel';
import { ApprovalQueue } from './components/ApprovalQueue';
import { ActivationChecklist } from './components/ActivationChecklist';

// ============================================================================
// GraphQL Operations
// ============================================================================

// ============================================================================
// Types
// ============================================================================

interface SystemTenantRow {
  tenantId: string;
  name: string;
  domain: string;
  industry: string;
  complianceFrameworks: string[];
  subscriptionTier: string;
  maxUsers: number;
  maxWorkflows: number;
  isActive: boolean;
  onboardingStatus: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'DELETED';
  primaryContactName: string;
  primaryContactEmail: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Status badge config (data-driven from backend enum values)
// ============================================================================

const STATUS_CONFIG: Record<string, { color: string; icon: typeof IconClock }> = {
  PENDING: { color: 'yellow', icon: IconClock },
  ACTIVE: { color: 'green', icon: IconChecks },
  SUSPENDED: { color: 'red', icon: IconBan },
  DELETED: { color: 'gray', icon: IconBan },
};

// ============================================================================
// Component
// ============================================================================

export function TenantManagementPage(): React.ReactElement {
  const { user } = useAuth();
  const viewLayer = useViewLayer();
  const tenantId = useTenantId();

  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  // Fetch all system tenants
  const { data, loading, error, refetch } = useQuery<Record<string, any>>(ActiveSystemTenantsDocument, {
    skip: !tenantId || viewLayer !== 'architect',
  });

  const [activateTenant, { loading: activating }] = useMutation<Record<string, any>>(ActivateSystemTenantDocument);

  const tenants: SystemTenantRow[] = useMemo(
    () => data?.activeSystemTenants ?? [],
    [data],
  );

  const selectedTenant = useMemo(
    () => tenants.find((t) => t.tenantId === selectedTenantId) ?? null,
    [tenants, selectedTenantId],
  );

  // Summary stats
  const stats = useMemo(() => ({
    total: tenants.length,
    pending: tenants.filter((t) => t.onboardingStatus === GqlOnboardingStatus.PENDING).length,
    active: tenants.filter((t) => t.onboardingStatus === GqlOnboardingStatus.ACTIVE).length,
    suspended: tenants.filter((t) => t.onboardingStatus === GqlOnboardingStatus.SUSPENDED).length,
  }), [tenants]);

  const handleActivate = useCallback(async (tid: string) => {
    await activateTenant({ variables: { tenantId: tid } });
    await refetch();
  }, [activateTenant, refetch]);

  // ========================================
  // Access control: architect view only
  // ========================================
  if (viewLayer !== 'architect') {
    return (
      <Container size="xl" py="xl">
        <Alert
          color="red"
          title="Access Denied"
          icon={<IconAlertCircle size={16} />}
        >
          Tenant management requires Architect view layer (superadmin or architect role).
        </Alert>
      </Container>
    );
  }

  // Loading
  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="md">
          <Skeleton height={40} />
          <SimpleGrid cols={4}>
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
          </SimpleGrid>
          <Skeleton height={300} />
        </Stack>
      </Container>
    );
  }

  // Error
  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert
          color="red"
          title="Failed to Load Tenants"
          icon={<IconAlertCircle size={16} />}
        >
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Group gap="xs">
              <Title order={2}>Tenant Management</Title>
              <Badge variant="light" color="violet" size="sm">DRR-073</Badge>
            </Group>
            <Text c="dimmed" size="sm">
              Onboard and manage customer tenants. FERP Operations (superadmin).
            </Text>
          </div>
        </Group>

        {/* Summary cards */}
        <SimpleGrid cols={{ base: 2, md: 4 }}>
          <StatCard
            label="Total Tenants"
            value={stats.total}
            color="blue"
            icon={<IconBuilding size={20} />}
          />
          <StatCard
            label="Pending Onboarding"
            value={stats.pending}
            color="yellow"
            icon={<IconClock size={20} />}
          />
          <StatCard
            label="Active"
            value={stats.active}
            color="green"
            icon={<IconChecks size={20} />}
          />
          <StatCard
            label="Suspended"
            value={stats.suspended}
            color="red"
            icon={<IconBan size={20} />}
          />
        </SimpleGrid>

        {/* Tenant list or detail view */}
        {selectedTenant ? (
          <TenantOnboardingDetail
            tenant={selectedTenant}
            onBack={() => setSelectedTenantId(null)}
            onActivate={handleActivate}
            activating={activating}
          />
        ) : (
          <TenantTable
            tenants={tenants}
            onSelect={setSelectedTenantId}
          />
        )}
      </Stack>
    </Container>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function StatCard({
  label,
  value,
  color,
  icon,
}: Readonly<{
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
}>): React.ReactElement {
  return (
    <Card withBorder padding="md">
      <Group justify="space-between">
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            {label}
          </Text>
          <Text fw={700} size="xl">
            {value}
          </Text>
        </div>
        <ThemeIcon size="lg" variant="light" color={color}>
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );
}

function TenantTable({
  tenants,
  onSelect,
}: Readonly<{
  tenants: SystemTenantRow[];
  onSelect: (id: string) => void;
}>): React.ReactElement {
  return (
    <Card withBorder>
      <Text fw={600} mb="md">All Tenants</Text>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Tenant</Table.Th>
            <Table.Th>Industry</Table.Th>
            <Table.Th>Tier</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Compliance</Table.Th>
            <Table.Th>Contact</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tenants.map((t) => {
            const statusCfg = STATUS_CONFIG[t.onboardingStatus] ?? STATUS_CONFIG.PENDING;
            return (
              <Table.Tr key={t.tenantId}>
                <Table.Td>
                  <div>
                    <Text size="sm" fw={600}>{t.name}</Text>
                    <Text size="xs" c="dimmed">{t.domain}</Text>
                  </div>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{t.industry}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" size="sm">
                    {t.subscriptionTier}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge color={statusCfg?.color} variant="light" size="sm">
                    {t.onboardingStatus}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {t.complianceFrameworks.slice(0, 2).map((fw) => (
                      <Badge key={fw} size="xs" variant="outline" color="gray">
                        {fw}
                      </Badge>
                    ))}
                    {t.complianceFrameworks.length > 2 && (
                      <Badge size="xs" variant="outline" color="gray">
                        +{t.complianceFrameworks.length - 2}
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{t.primaryContactEmail}</Text>
                </Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => onSelect(t.tenantId)}
                  >
                    Manage
                  </Button>
                </Table.Td>
              </Table.Tr>
            );
          })}
          {tenants.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Text c="dimmed" ta="center" py="lg">
                  No tenants found. Use the CLI to create a tenant:
                  python3 ./scripts/onboard-tenant.py
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

/** Detailed onboarding panel for a single tenant */
function TenantOnboardingDetail({
  tenant,
  onBack,
  onActivate,
  activating,
}: Readonly<{
  tenant: SystemTenantRow;
  onBack: () => void;
  onActivate: (tenantId: string) => void;
  activating: boolean;
}>): React.ReactElement {
  const statusCfg = STATUS_CONFIG[tenant.onboardingStatus] ?? STATUS_CONFIG.PENDING;

  // Onboarding progress (simplified: Pending=25%, Active=100%, Suspended=50%)
  const progressMap: Record<string, number> = {
    PENDING: 25,
    ACTIVE: 100,
    SUSPENDED: 50,
    DELETED: 0,
  };
  const progress = progressMap[tenant.onboardingStatus] ?? 0;

  return (
    <Stack gap="md">
      {/* Back button + header */}
      <Group justify="space-between">
        <Group gap="sm">
          <Button variant="subtle" size="sm" onClick={onBack}>
            Back to list
          </Button>
          <Title order={3}>{tenant.name}</Title>
          <Badge color={statusCfg?.color} variant="light">
            {tenant.onboardingStatus}
          </Badge>
        </Group>
        {tenant.onboardingStatus === GqlOnboardingStatus.PENDING && (
          <Button
            color="green"
            leftSection={<IconRocket size={16} />}
            loading={activating}
            onClick={() => onActivate(tenant.tenantId)}
          >
            Activate Tenant
          </Button>
        )}
      </Group>

      {/* Tenant info card */}
      <Card withBorder>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Domain</Text>
            <Text size="sm">{tenant.domain}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Industry</Text>
            <Text size="sm">{tenant.industry}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Contact</Text>
            <Text size="sm">{tenant.primaryContactName}</Text>
            <Text size="xs" c="dimmed">{tenant.primaryContactEmail}</Text>
          </div>
        </SimpleGrid>
        <Group mt="md" gap="xs">
          <Text size="xs" c="dimmed">Compliance:</Text>
          {tenant.complianceFrameworks.map((fw) => (
            <Badge key={fw} size="sm" variant="light" color="gray">{fw}</Badge>
          ))}
        </Group>
      </Card>

      {/* Onboarding progress */}
      <Card withBorder>
        <Text fw={600} mb="xs">Onboarding Progress</Text>
        <Progress value={progress} color={statusCfg?.color} size="lg" mb="xs" />
        <Text size="xs" c="dimmed">
          {progress < 100
            ? 'Complete all onboarding steps to activate this tenant.'
            : 'Tenant is fully onboarded and active.'}
        </Text>
      </Card>

      {/* Onboarding tabs: Upload / Approval / Activation */}
      <Tabs defaultValue="upload">
        <Tabs.List>
          <Tabs.Tab
            value="upload"
            leftSection={<IconUpload size={14} />}
          >
            Document Upload
          </Tabs.Tab>
          <Tabs.Tab
            value="approvals"
            leftSection={<IconClipboardCheck size={14} />}
          >
            Approval Queue
          </Tabs.Tab>
          <Tabs.Tab
            value="activation"
            leftSection={<IconListCheck size={14} />}
          >
            Activation Checklist
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="upload" pt="md">
          <DocumentUploadPanel tenantId={tenant.tenantId} tenantName={tenant.name} />
        </Tabs.Panel>

        <Tabs.Panel value="approvals" pt="md">
          <ApprovalQueue tenantId={tenant.tenantId} />
        </Tabs.Panel>

        <Tabs.Panel value="activation" pt="md">
          <ActivationChecklist
            tenantId={tenant.tenantId}
            onboardingStatus={tenant.onboardingStatus}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

export default TenantManagementPage;
